import api from './api';

export interface KPIStat {
  value: number;
  change: number;
  sparkline: number[];
}

export interface DashboardStats {
  impressions: KPIStat;
  clicks: KPIStat;
  ctr: KPIStat;
  cpc: KPIStat;
  conversions: KPIStat;
  roas: KPIStat;
  spend: KPIStat;
  roi: KPIStat;
  revenue: KPIStat;
}

export interface ChartDataPoint {
  date: string;
  impressions: number;
  clicks: number;
  conversions: number;
  spend: number;
  revenue: number;
  ctr: number;
  cpc: number;
  roas: number;
}

export interface PlatformData {
  platform: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number;
  roas: number;
  color: string;
}

export interface ChartData {
  performance: ChartDataPoint[];
  platforms: PlatformData[];
}

export interface InsightMetric {
  label: string;
  value: string;
  trend?: 'up' | 'down';
}

export interface Insight {
  id: string;
  type: 'opportunity' | 'warning' | 'trend' | 'recommendation';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  actionUrl?: string;
  actionLabel?: string;
  metrics?: InsightMetric[];
}

export interface Alert {
  id: string;
  type: 'error' | 'warning' | 'success' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

export interface TopKeyword {
  id: string;
  keyword: string;
  search_volume: number;
  difficulty: number;
  cpc: number;
  competition: string;
  current_position: number | null;
  trend: 'up' | 'down' | 'stable';
}

export interface TopCampaign {
  id: string;
  name: string;
  platform: string;
  status: string;
  total_impressions: number;
  total_clicks: number;
  total_conversions: number;
  total_spent: number;
  cpa: number | null;
  currency?: string;
}

export interface Activity {
  id: string;
  type: 'success' | 'warning' | 'info' | 'campaign' | 'content' | 'competitor';
  title: string;
  description?: string;
  timestamp: string;
  link?: string;
}

export interface ScheduledPost {
  id: string;
  content: string;
  platforms: string[];
  scheduledAt: string;
  status: 'scheduled' | 'suggested' | 'draft';
  aiGenerated?: boolean;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

interface StatsResponse {
  success: boolean;
  data: DashboardStats;
  period: string;
  lastUpdated: string;
}

interface ChartsResponse {
  success: boolean;
  data: ChartData;
  period: string;
}

interface InsightsResponse {
  success: boolean;
  data: Insight[];
}

interface AlertsResponse {
  success: boolean;
  data: Alert[];
}

interface TopKeywordsResponse {
  success: boolean;
  data: TopKeyword[];
}

interface TopCampaignsResponse {
  success: boolean;
  data: TopCampaign[];
}

export interface TopSearchTerm {
  search_term: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
}

interface TopSearchTermsResponse {
  success: boolean;
  data: TopSearchTerm[];
}

export interface TopSpendingCampaign {
  id: string;
  name: string;
  platform: string;
  status: string;
  currency: string;
  spend_today: number;
  clicks_today: number;
  impressions_today: number;
  conversions_today: number;
  cpa_today: number | null;
  budget_amount: number | null;
  budget_type: 'daily' | 'lifetime' | null;
  budget_used_pct: number | null;
}

interface TopSpendingTodayResponse {
  success: boolean;
  data: TopSpendingCampaign[];
}

interface ActivitiesResponse {
  success: boolean;
  data: Activity[];
}

interface UpcomingPostsResponse {
  success: boolean;
  data: ScheduledPost[];
}

// Helper to build headers with optional project override (supports single ID, array, or 'all')
const projectHeaders = (projectId?: string | string[] | null) => {
  if (projectId === 'all') return { 'X-Project-ID': '' };
  if (Array.isArray(projectId)) return { 'X-Project-ID': projectId.join(',') };
  if (projectId) return { 'X-Project-ID': projectId };
  return {};
};

export const dashboardService = {
  /**
   * Get aggregated KPI stats for the dashboard
   */
  getStats: async (dateRange: DateRange, projectId?: string | string[] | null): Promise<StatsResponse> => {
    const params = new URLSearchParams({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    const response = await api.get<StatsResponse>(`/dashboard/stats?${params}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get chart data for performance visualization
   */
  getCharts: async (dateRange: DateRange, projectId?: string | string[] | null): Promise<ChartsResponse> => {
    const params = new URLSearchParams({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    const response = await api.get<ChartsResponse>(`/dashboard/charts?${params}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get AI-generated insights
   */
  getInsights: async (projectId?: string | string[] | null): Promise<InsightsResponse> => {
    const response = await api.get<InsightsResponse>('/dashboard/insights', { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get active alerts
   */
  getAlerts: async (projectId?: string | string[] | null): Promise<AlertsResponse> => {
    const response = await api.get<AlertsResponse>('/dashboard/alerts', { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get top performing keywords
   */
  getTopKeywords: async (limit: number = 5, projectId?: string | string[] | null): Promise<TopKeywordsResponse> => {
    const response = await api.get<TopKeywordsResponse>(`/dashboard/top-keywords?limit=${limit}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get top performing campaigns
   */
  getTopCampaigns: async (limit: number = 5, projectId?: string | string[] | null): Promise<TopCampaignsResponse> => {
    const response = await api.get<TopCampaignsResponse>(`/dashboard/top-campaigns?limit=${limit}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get top search terms (real queries that triggered ads in last 30 days)
   */
  getTopSearchTerms: async (limit: number = 5, projectId?: string | string[] | null): Promise<TopSearchTermsResponse> => {
    const response = await api.get<TopSearchTermsResponse>(`/dashboard/top-search-terms?limit=${limit}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get campaigns that spent the most today
   */
  getTopSpendingToday: async (limit: number = 5, projectId?: string | string[] | null): Promise<TopSpendingTodayResponse> => {
    const response = await api.get<TopSpendingTodayResponse>(`/dashboard/top-spending-today?limit=${limit}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get recent activities
   */
  getActivities: async (limit: number = 10, projectId?: string | string[] | null): Promise<ActivitiesResponse> => {
    const response = await api.get<ActivitiesResponse>(`/dashboard/activities?limit=${limit}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get upcoming scheduled posts
   */
  getUpcomingPosts: async (limit: number = 5, projectId?: string | string[] | null): Promise<UpcomingPostsResponse> => {
    const response = await api.get<UpcomingPostsResponse>(`/dashboard/upcoming-posts?limit=${limit}`, { headers: projectHeaders(projectId) });
    return response.data;
  },

  /**
   * Get all dashboard data at once
   */
  getAllData: async (dateRange: DateRange, projectId?: string | string[] | null) => {
    const pid = projectId;
    const [stats, charts, insights, topSpendingToday, topSearchTerms, topCampaigns, activities, upcomingPosts] = await Promise.all([
      dashboardService.getStats(dateRange, pid),
      dashboardService.getCharts(dateRange, pid),
      dashboardService.getInsights(pid),
      dashboardService.getTopSpendingToday(5, pid),
      dashboardService.getTopSearchTerms(5, pid),
      dashboardService.getTopCampaigns(5, pid),
      dashboardService.getActivities(10, pid),
      dashboardService.getUpcomingPosts(5, pid)
    ]);

    return {
      stats: stats.data,
      charts: charts.data,
      insights: insights.data,
      topSpendingToday: topSpendingToday.data,
      topSearchTerms: topSearchTerms.data,
      topCampaigns: topCampaigns.data,
      activities: activities.data,
      upcomingPosts: upcomingPosts.data,
      lastUpdated: stats.lastUpdated
    };
  },

  /**
   * Export report as PDF/Excel/CSV
   */
  exportReport: async (format: 'pdf' | 'xlsx' | 'csv', dateRange: DateRange): Promise<Blob> => {
    const params = new URLSearchParams({
      format,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      type: 'performance'
    });
    const response = await api.get(`/reports/export?${params}`, { responseType: 'blob' });
    return response.data;
  },

  /**
   * Export dashboard report as PDF
   */
  exportDashboardPDF: async (dateRange: DateRange, projectId?: string | string[] | null, sections?: string[]): Promise<Blob> => {
    const params = new URLSearchParams({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    });
    if (sections && sections.length > 0) {
      params.set('sections', sections.join(','));
    }
    const response = await api.get(`/reports/dashboard-pdf?${params}`, { responseType: 'blob', headers: projectHeaders(projectId) });
    return response.data;
  }
};

export default dashboardService;
